import { apiRequest } from '@/services/http'

/**
 * `bs-core`'s shared vocabulary collections — same shape core-service's `/v1/metadata` returns.
 * `_id` is always present (Mongoose includes it unless explicitly excluded) but only `skills`
 * matters for it: `bs-talent.profiles.skills[].skillId` refs `Skill._id` directly, unlike every
 * other category here, which is referenced by `elId`.
 */
export interface MetadataItem {
  _id?: string
  elId: number
  value: string
  valueHeb?: string
}

export function getSkills(): Promise<MetadataItem[]> {
  return apiRequest('/v1/metadata/skills')
}

export function getRoles(): Promise<MetadataItem[]> {
  return apiRequest('/v1/metadata/roles')
}

export function getIndustries(): Promise<MetadataItem[]> {
  return apiRequest('/v1/metadata/industries')
}

export interface CityItem {
  cityId: number
  city: string
  cityAlt?: string[]
  regionId?: number
  country?: string
}

/**
 * `q` prefix-searches (debounce on the caller's side — this hits the network every call), `ids`
 * hydrates a known set of cityIds with no search term. Neither → `[]`; there is no "browse all
 * 722" mode, matching legacy's location autocomplete (`useLocationsSearchQuery`), which never
 * loaded the full table either.
 */
export function getCities(params: { q?: string; ids?: number[] }): Promise<CityItem[]> {
  const search = new URLSearchParams()
  if (params.q) search.set('q', params.q)
  if (params.ids?.length) search.set('ids', params.ids.join(','))
  const query = search.toString()
  return apiRequest(`/v1/metadata/cities${query ? `?${query}` : ''}`)
}

export interface CompanyItem {
  _id: string
  name: string
  logoUrl?: string
  slug?: string
}

/**
 * `bs-core.companies` — per-tenant CRM data everywhere else, read cross-tenant here (name+logo
 * only) so a talent's job-history "Company" field can search "does a company by this name exist
 * anywhere" the way legacy's autocomplete did. Same q/ids shape as `getCities` — `ids` takes
 * Mongo ObjectId strings instead of a numeric elId.
 */
export function getCompanies(params: { q?: string; ids?: string[] }): Promise<CompanyItem[]> {
  const search = new URLSearchParams()
  if (params.q) search.set('q', params.q)
  if (params.ids?.length) search.set('ids', params.ids.join(','))
  const query = search.toString()
  return apiRequest(`/v1/metadata/companies${query ? `?${query}` : ''}`)
}

/** The company DETAIL page's wider (still public-safe) projection — see `companyBySlug()`'s own comment. */
export interface CompanyDetail {
  _id: string
  slug: string
  name: string
  logoUrl?: string
  description?: string
  industries?: number[]
  sizeId?: number
  numberOfEmployees?: number
  foundedYear?: number
  location?: { cityId?: number; country?: string }
  contact?: {
    website?: string
    /** `type` is meant to be a `communication` elId, but real rows don't reliably use it that way
     * — find the LinkedIn one by URL substring, not by `type` (see `companyBySlug()`'s comment). */
    socialLinks?: Array<{ url: string; type?: number }>
  }
  specialties?: Array<{ id?: number; value: string }>
  businessModel?: number[]
  companyMarket?: number[]
  /** Resolves against the `companyBusinessStage` lookup category — an ownership-type value
   * (Publicly Held / Privately Held / …), despite the field's own name. */
  businessStage?: number
}

export function getCompanyBySlug(slug: string): Promise<CompanyDetail> {
  return apiRequest(`/v1/metadata/companies/${slug}`)
}

/**
 * The long tail — `positionTypes`/`companySizes`/`workspaceTypes`/`currency`/`seniorityLevels`
 * all come through this one route. `core-service`'s controller doesn't project `order`, so this
 * returns exactly `{elId, value, valueHeb}` — a few categories (seniorityLevels in particular)
 * have junk test rows seeded alongside the real ones with no field here to tell them apart by;
 * callers that need a clean set filter by a known-good label allowlist (see
 * `profile-page.tsx`'s `SENIORITY_ALLOWLIST`).
 */
export function getLookup(category: string): Promise<MetadataItem[]> {
  return apiRequest(`/v1/metadata/lookup/${category}`)
}

/** Resolves one raw elId against a fetched vocabulary — `undefined` for an id the lookup doesn't have. */
export function labelFor(lookup: MetadataItem[], id: number | undefined): string | undefined {
  if (id === undefined) return undefined
  return lookup.find((item) => item.elId === id)?.value
}

/** Same lookup, for a job/profile field that stores several elIds (e.g. industries, skills). */
export function labelsFor(lookup: MetadataItem[], ids: number[] | undefined): string[] {
  if (!ids?.length) return []
  return ids.flatMap((id) => {
    const match = lookup.find((item) => item.elId === id)
    return match ? [match.value] : []
  })
}

/** A fetched vocabulary, shaped for `SearchPicker`/`TagChoices` — same `{id, label}` pair every
 *  select-style picker in this app expects. */
export function toSelectOptions(items: MetadataItem[]): { id: string; label: string }[] {
  return items.map((item) => ({ id: String(item.elId), label: item.value }))
}

/** "City, Country" for a resolved `CityItem` — empty parts drop out rather than leaving a stray comma. */
export function formatLocation(city: { city?: string } | null | undefined, country: string | undefined): string {
  return [city?.city, country].filter(Boolean).join(', ')
}
